import type {
  PlaceholderClaimDto,
  PlaceholderClaimSummaryDto,
} from '@splitfiesta/shared';

type Viewable = {
  userId: string;
  isPlaceholder?: boolean;
  createdBy?: string;
  claim?: PlaceholderClaimSummaryDto;
};

type Role = 'owner' | 'admin' | 'member' | undefined;

/** A placeholder and the member who claims to be that person. */
export interface PendingClaimPair {
  placeholderId: string;
  claimantId: string;
}

export interface PlaceholderMemberActions {
  canRename: boolean;
  canRemove: boolean;
  canPromote: boolean;
  canDemote: boolean;
  canReport: boolean;
}

/**
 * Which claim block, if any, a viewer gets under a placeholder row: the
 * merge's progress line, the owner's repair pill, or the claim pill.
 */
export type PlaceholderClaimBlockState =
  | 'none'
  // Nobody claims this placeholder and the viewer is free to: the row offers
  // them the pill that says "this is me".
  | 'claimable'
  | 'merging'
  // Belongs to every decider, admins included.
  | 'ownerRetry'
  | 'failed';

/** The pills a claim block may offer, in the order they are rendered. */
export type PlaceholderClaimAction = 'retry' | 'claim';

export interface ClaimBlockInput {
  claim: PlaceholderClaimSummaryDto | null | undefined;
  viewerId: string;
  userRole?: 'owner' | 'admin' | 'member';
  /**
   * Whether the viewer may not claim another placeholder here (see
   * viewerCannotClaim). One claim per claimant per membership is an API rule,
   * so an unclaimed row only offers the pill while this is false.
   */
  viewerCannotClaim?: boolean;
}

const MAX_NAME_LENGTH = 50;

/**
 * Single source of truth for what a viewer may do with a placeholder row, so
 * web and mobile cannot drift on which actions are hidden.
 */
export class PlaceholderMemberService {
  /**
   * A merge runs server-side with nothing to push the result back, so the
   * roster polls at this pace while one is in flight.
   */
  static readonly CLAIM_MERGE_POLL_MS = 5000;

  /**
   * Whether a merge on this roster can still finish by itself. A row past its
   * lease (`canRetry`) only changes when a decider repairs it, so polling it
   * would change nothing.
   */
  static isMergeInFlight(
    rows: Array<{
      claim?: Pick<PlaceholderClaimDto, 'status' | 'canRetry'> | null;
    }>
  ): boolean {
    return rows.some(
      ({ claim }) =>
        (claim?.status === 'merging' || claim?.status === 'pending') &&
        claim.canRetry === false
    );
  }

  static isPlaceholder(member: { isPlaceholder?: boolean }): boolean {
    return member.isPlaceholder === true;
  }

  static canManage(userRole: Role): boolean {
    return userRole === 'owner' || userRole === 'admin';
  }

  /**
   * Any member may add one, the same way any member may share the invite
   * link.
   */
  static canAdd(userRole: Role): boolean {
    return userRole !== undefined;
  }

  /**
   * Owner, admin, or the member who added it. A row without `createdBy`
   * predates the field and stays with the owner and admins.
   */
  static canManagePlaceholder(
    member: Viewable,
    viewerId: string,
    userRole: Role
  ): boolean {
    if (this.canManage(userRole)) return true;
    return (
      userRole !== undefined &&
      !!member.createdBy &&
      member.createdBy === viewerId
    );
  }

  /**
   * Admins repair a stuck merge alongside the owner (the API gates it the same
   * way) so a group whose owner is away is never stuck with a failed claim.
   */
  static canDecideClaims(userRole: Role): boolean {
    return userRole === 'owner' || userRole === 'admin';
  }

  /**
   * Every member sees an open claim: the pickers treat the pair as one person,
   * so hiding the reason from bystanders made the app look broken to them.
   * Retrying stays with `canDecideClaims`.
   */
  static visibleClaim(member: Viewable): PlaceholderClaimSummaryDto | null {
    return member.claim ?? null;
  }

  static memberActions(
    member: Viewable,
    viewerId: string,
    userRole: Role
  ): PlaceholderMemberActions {
    if (!this.isPlaceholder(member)) {
      // Real members keep the existing per-platform rules; only `canReport`
      // is asserted here so callers can share one shape.
      return {
        canRename: false,
        canRemove: false,
        canPromote: false,
        canDemote: false,
        canReport: member.userId !== viewerId,
      };
    }

    const manage = this.canManagePlaceholder(member, viewerId, userRole);
    return {
      canRename: manage,
      canRemove: manage,
      // A placeholder has no account, so it can hold no role and there is
      // nobody behind it to report.
      canPromote: false,
      canDemote: false,
      canReport: false,
    };
  }

  /**
   * The table both platforms render inside a placeholder row. An unclaimed
   * row is `claimable` for a viewer who may still claim here, never for the
   * owner; a claimed row shows the merge's progress or, once it is stuck, the
   * repair pill to a decider and the failure to everyone else.
   */
  static claimBlockState(input: ClaimBlockInput): PlaceholderClaimBlockState {
    const { claim, viewerId, userRole, viewerCannotClaim } = input;
    // Opt-in: a caller that says nothing about the viewer's other claims (an
    // orphan claim row, say) gets the old claim-only behaviour.
    //
    // The owner created the group with a full account and was never a
    // placeholder, so a claim by them could only fold someone else's history
    // into their own.
    if (!claim) {
      return viewerCannotClaim === false && userRole !== 'owner'
        ? 'claimable'
        : 'none';
    }

    // A stored 'pending' row predates the claim that merges at once; it reads
    // as 'merging' until the server's `canRetry` says the merge is stuck.
    if (claim.status !== 'failed' && !claim.canRetry) return 'merging';

    // Nobody repairs the claim that names them (the API refuses it with
    // `ClaimSelfDecisionError`), so a decider who is the claimant sees what
    // every other member sees.
    return this.canDecideClaims(userRole) && claim.claimantId !== viewerId
      ? 'ownerRetry'
      : 'failed';
  }

  /**
   * The `groups:placeholderMembers.*` key each state shows as the row's
   * second line, so web and mobile cannot word the same state differently.
   */
  static claimLineKey(
    state: PlaceholderClaimBlockState
  ): 'claimMerging' | 'claimFailedRetry' | 'claimFailed' | null {
    switch (state) {
      case 'merging':
        return 'claimMerging';
      case 'ownerRetry':
        return 'claimFailedRetry';
      case 'failed':
        return 'claimFailed';
      // 'claimable' is an offer, not news: the pill says everything.
      case 'claimable':
      default:
        return null;
    }
  }

  /**
   * The pills each state offers, so the two platforms cannot end up with
   * different buttons on the same row.
   */
  static claimActions(
    state: PlaceholderClaimBlockState
  ): PlaceholderClaimAction[] {
    switch (state) {
      // A stuck merge restarts through the confirm endpoint, so a retry is
      // the same call under a different label.
      case 'ownerRetry':
        return ['retry'];
      case 'claimable':
        return ['claim'];
      default:
        return [];
    }
  }

  /** What the host row needs before it draws its own chrome. */
  static claimRowPresentation(input: ClaimBlockInput): { canRetry: boolean } {
    return { canRetry: this.claimBlockState(input) === 'ownerRetry' };
  }

  /**
   * Claims the owner must still act on even though their placeholder row is
   * gone: a failed merge removes the member before the claim is resolved, so
   * these render as extra rows instead of vanishing.
   */
  static orphanClaims(
    claims: PlaceholderClaimDto[],
    members: Array<{ userId: string }>
  ): PlaceholderClaimDto[] {
    const memberIds = new Set(members.map(member => member.userId));
    return claims.filter(
      claim =>
        (claim.status === 'failed' || claim.status === 'merging') &&
        !memberIds.has(claim.placeholderId)
    );
  }

  /**
   * Narrows a list claim to the summary a claim block renders. The list DTO
   * also carries the two resolved statuses ('merged', 'rejected'), which no
   * block understands — those get null so the caller renders nothing.
   */
  static toClaimSummary(
    claim: PlaceholderClaimDto
  ): PlaceholderClaimSummaryDto | null {
    if (
      claim.status !== 'pending' &&
      claim.status !== 'merging' &&
      claim.status !== 'failed'
    ) {
      return null;
    }

    return {
      status: claim.status,
      claimantId: claim.claimantId,
      claimantName: claim.claimantName,
      canRetry: claim.canRetry,
    };
  }

  /**
   * Placeholder/claimant pairs that are the same person as far as the pickers
   * are concerned: an unfinished merge (`merging`, or a `pending` row left by
   * the retired approval flow) with both halves still in the group. A claim
   * whose claimant has since left is not a pair — the placeholder is the only
   * row left for that person.
   */
  static pendingClaimPairs(members: Viewable[]): PendingClaimPair[] {
    const memberIds = new Set(members.map(member => member.userId));
    const pairs: PendingClaimPair[] = [];

    for (const member of members) {
      const claim = member.claim;
      if (!this.isPlaceholder(member) || !claim) continue;
      if (claim.status !== 'pending' && claim.status !== 'merging') continue;
      if (!memberIds.has(claim.claimantId)) continue;
      pairs.push({
        placeholderId: member.userId,
        claimantId: claim.claimantId,
      });
    }

    return pairs;
  }

  /**
   * Whether this viewer may not claim a placeholder here — the one condition
   * that hides the Claim pill from every other placeholder row. True while
   * their claim is live or failed (the server refuses a second claim until
   * the repair), or once one was merged during their current membership: a
   * merge leaves no roster row to read, so the group's `viewerHasMergedClaim`
   * is a required argument. A rejected or withdrawn claim leaves neither, so
   * the pill returns.
   */
  static viewerCannotClaim(
    members: Viewable[],
    viewerId: string,
    viewerHasMergedClaim: boolean | undefined
  ): boolean {
    return (
      viewerHasMergedClaim === true ||
      members.some(
        ({ claim }) =>
          claim?.claimantId === viewerId && claim.status === 'failed'
      ) ||
      this.pendingClaimPairs(members).some(pair => pair.claimantId === viewerId)
    );
  }

  /**
   * For each paired placeholder, the name of the member claiming to be them —
   * the one lookup every picker needs, kept next to the pairing rule so web
   * and mobile cannot re-derive it differently.
   */
  static pendingClaimNames(members: Viewable[]): Map<string, string> {
    const paired = new Set(
      this.pendingClaimPairs(members).map(pair => pair.placeholderId)
    );

    const names = new Map<string, string>();
    for (const member of members) {
      if (member.claim && paired.has(member.userId)) {
        names.set(member.userId, member.claim.claimantName);
      }
    }
    return names;
  }

  /**
   * Who a NEW expense pre-selects: everyone except the placeholder half of a
   * merging pair, so one person is not charged twice while the merge runs.
   * The dropped row stays in the picker — it can still be picked deliberately.
   */
  static defaultParticipants(members: Viewable[]): string[] {
    const dropped = new Set(
      this.pendingClaimPairs(members).map(pair => pair.placeholderId)
    );
    return members
      .map(member => member.userId)
      .filter(userId => !dropped.has(userId));
  }

  /** Placeholder ids from a roster, for badging rows that carry no flag. */
  static placeholderIdSet(
    rows: Array<{ userId: string; isPlaceholder?: boolean }>
  ): Set<string> {
    return new Set(
      rows.filter(row => this.isPlaceholder(row)).map(row => row.userId)
    );
  }

  static validateName(name: string): { valid: boolean; trimmed: string } {
    const trimmed = name.trim();
    return {
      valid: trimmed.length > 0 && trimmed.length <= MAX_NAME_LENGTH,
      trimmed,
    };
  }
}
